import { useCallback, useEffect, useRef, useState } from "react";

import { useSession } from "@/src/contexts/AuthContext";
import { useLocale } from "@/src/i18n/i18n";
import type { GeocodingResult } from "@/src/libs/geocoding";
import { searchAddresses } from "@/src/libs/geocoding";

const DEBOUNCE_MS = 400;

/** En dessous, la recherche renvoie surtout du bruit. */
const MIN_QUERY_LENGTH = 3;

/**
 * État d'une recherche d'adresse : le texte saisi, les suggestions, et le
 * debounce qui évite une requête par caractère.
 *
 * ⚠️ **Portage de `wargame-app`**, à la mécanique près : mêmes garde-fous
 * contre les réponses dans le désordre, même annulation, même debounce. Ce qui
 * change tient à l'environnement — pas de React Native ici — et non à la
 * logique.
 *
 * La position enregistrée du compte connecté, quand il y en a une, sert à
 * privilégier ce qui est proche : « lyon 3 » remonte alors l'arrondissement
 * plutôt qu'une rue homonyme à l'autre bout du monde. Elle ne coûte aucune
 * requête supplémentaire.
 */
export function useAddressSearch(initialQuery?: string) {
  const { locale } = useLocale();
  const { session } = useSession();

  // ⚠️ Extraire avant de dépendre : une propriété optionnelle dans un tableau
  // de dépendances fait perdre la mémoïsation de tout le composant appelant.
  const latitude = session?.latitude ?? null;
  const longitude = session?.longitude ?? null;

  const [query, setQuery] = useState(() => initialQuery ?? "");
  const [suggestions, setSuggestions] = useState<GeocodingResult[]>([]);
  const [isFetching, setIsFetching] = useState(false);

  /**
   * Une recherche est allée au bout et n'a rien rendu.
   *
   * ⚠️ **Sans ce drapeau, « aucun lieu ne correspond » s'affiche après un
   * choix** : sélectionner « Lyon » vide les suggestions et laisse le libellé
   * dans le champ, ce qui est exactement la forme d'une recherche
   * infructueuse. On distinguait donc le succès de l'échec par le même état.
   */
  const [isEmpty, setIsEmpty] = useState(false);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  /**
   * Numéro de la dernière requête émise. Deux recherches lancées coup sur coup
   * peuvent revenir dans le désordre — la plus lente écrasant alors la plus
   * récente, et l'utilisateur voit des suggestions pour un texte qu'il a déjà
   * corrigé. Seule la requête dont le numéro est encore courant a le droit
   * d'écrire.
   */
  const requestIdRef = useRef(0);

  // Un timer ou une requête encore en vol après le démontage rappelleraient
  // `setState` sur un composant disparu.
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      abortRef.current?.abort();
    };
  }, []);

  const fetchSuggestions = useCallback(
    async (text: string) => {
      if (text.trim().length < MIN_QUERY_LENGTH) {
        setSuggestions([]);
        setIsEmpty(false);
        return;
      }

      // La requête précédente n'intéresse plus personne : l'annuler libère la
      // connexion et évite de faire travailler un service public gratuit pour
      // un résultat qu'on jetterait.
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      const requestId = ++requestIdRef.current;

      setIsFetching(true);
      try {
        const results = await searchAddresses(text, locale, {
          bias:
            latitude !== null && longitude !== null
              ? { latitude, longitude }
              : null,
          signal: controller.signal,
        });
        if (requestId !== requestIdRef.current) return;
        setSuggestions(results);
        setIsEmpty(results.length === 0);
      } catch {
        // Réseau indisponible, service en erreur, ou requête annulée par la
        // frappe suivante : liste vide. La recherche repart à la frappe
        // d'après, un toast serait du bruit.
        if (requestId !== requestIdRef.current) return;
        setSuggestions([]);
        setIsEmpty(false);
      } finally {
        // Une requête périmée ne rend pas la main sur l'indicateur : celle qui
        // l'a supplantée est encore en cours.
        if (requestId === requestIdRef.current) setIsFetching(false);
      }
    },
    [locale, latitude, longitude],
  );

  /** On affiche tout de suite, on cherche 400 ms plus tard. */
  const handleChangeText = useCallback(
    (text: string) => {
      setQuery(text);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        fetchSuggestions(text);
      }, DEBOUNCE_MS);
    },
    [fetchSuggestions],
  );

  /** Une suggestion a été choisie : on fige le texte sans relancer de recherche. */
  const commit = useCallback((label: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setQuery(label);
    setSuggestions([]);
    setIsEmpty(false);
  }, []);

  /**
   * Remise à zéro. Annule le debounce en vol, comme `commit` : sinon une
   * recherche lancée par la dernière frappe atterrit après la remise à zéro et
   * rouvre des suggestions sur un champ vide.
   */
  const reset = useCallback(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setQuery("");
    setSuggestions([]);
    setIsEmpty(false);
  }, []);

  return {
    query,
    suggestions,
    isFetching,
    isEmpty,
    handleChangeText,
    commit,
    reset,
  };
}
